import { Box, Button, Dialog, DialogActions, Stack, Typography } from "@mui/material";
import RenderInputField from "components/ReusableComponents/RenderInputField/RenderInputField";
import {
	footwearMaterialColumns,
	upperMaterialColumn,
	upperMaterialTypeOptions,
	upperMaterialPatternOptions,
	soleMaterialOptions,
	insocksOptions,
} from "../data";
import { arrType, inputTypes, selectOptionsKeyName } from "data/globalData";
import { useEffect, useState } from "react";
import { getColDefaultValue } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";

const CreateOrUpdateMaterialModal = ({
	open,
	setOpen,
	handleSaveFunction = () => {},
	mode = "create",
	selectedRow,
	setSelectedRow,
}) => {
	const { snackBar, setSnackBarOpen, setSnackBar } = GlobalState();

	const defPageData = {
		...Object.values(footwearMaterialColumns)
			.map((col) => ({ [col.key]: getColDefaultValue(col) }))
			.reduce((acc, row) => ({ ...acc, ...row }), {}),
		[upperMaterialColumn.key]: getColDefaultValue(upperMaterialColumn),
	};

	const [pageData, setPageData] = useState(defPageData);
	const [errorObj, setErrorObj] = useState({});

	useEffect(() => {
		if (mode === "update" && selectedRow) {
			setPageData(selectedRow);
		}
	}, [selectedRow, mode]);

	const handleCloseModal = () => {
		setOpen(false);
		setErrorObj({});
		setPageData(defPageData);
		setSelectedRow();
	};

	const handleSaveClick = () => {
		let errObj = {};

		if (!pageData[upperMaterialColumn.key]) {
			errObj = { ...errObj, [upperMaterialColumn.key]: `${upperMaterialColumn.name} is required` };
		}

		Object.values(footwearMaterialColumns).forEach((col) => {
			if (col.input_type === inputTypes.AUTO_GENERATE.key) return;
			if (col.constraints.NOTNULL && !pageData[col.key]) {
				errObj = { ...errObj, [col.key]: `${col.name} is required` };
			}
		});

		if (Object.keys(errObj).length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "warning",
				message: `Please fill the required fields`,
			});
			setErrorObj(errObj);
			return;
		}

		setErrorObj({});
		if (typeof handleSaveFunction === "function") handleSaveFunction(pageData);
	};

	return (
		<Dialog fullWidth maxWidth="md" open={open} onClose={handleCloseModal} scroll="body">
			<Stack width="100%" p={2} boxSizing="border-box" spacing={2}>
				<Typography variant="h6">{mode === "create" ? "Create" : "Update"} Footwear Material</Typography>

				<Box display="flex" width="100%" rowGap={2} flexWrap="wrap" columnGap="3%">
					<RenderInputField
						column={footwearMaterialColumns.fw_material_code}
						data={pageData}
						setData={() => {}}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.fw_material_code.key]}
					/>
					<RenderInputField
						column={{
							...upperMaterialColumn,
							input_type: mode === "create" ? inputTypes.SELECT.key : inputTypes.AUTO_GENERATE.key,
						}}
						data={pageData}
						setData={setPageData}
						onChangeCallBack={(e, value, updated) => {
							setPageData({
								...updated,
								[footwearMaterialColumns.upper_material_type.key]: "",
								[footwearMaterialColumns.upper_material_pattern.key]: "",
								[footwearMaterialColumns.sole_material.key]: "",
								[footwearMaterialColumns.insocks.key]: "",
							});
						}}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[upperMaterialColumn.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.upper_material_type,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? upperMaterialTypeOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.upper_material_type.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.upper_material_pattern,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? upperMaterialPatternOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.upper_material_pattern.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.sole_material,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? soleMaterialOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.sole_material.key]}
					/>
					<RenderInputField
						column={{
							...footwearMaterialColumns.insocks,
							[selectOptionsKeyName]: {
								type: arrType,
								list: pageData?.[upperMaterialColumn.key]
									? insocksOptions.filter(
											(item) => item[upperMaterialColumn.key] === pageData[upperMaterialColumn.key],
										)
									: [],
							},
						}}
						data={pageData}
						setData={setPageData}
						selectOptObjName={selectOptionsKeyName}
						width="30%"
						error={errorObj[footwearMaterialColumns.insocks.key]}
					/>
				</Box>
			</Stack>
			<DialogActions>
				<Button variant="outlined" onClick={handleCloseModal}>
					Cancel
				</Button>
				<Button autoFocus variant="contained" sx={{ mr: 1 }} onClick={handleSaveClick}>
					{mode === "create" ? "Create" : "Save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CreateOrUpdateMaterialModal;
